export type MessageType = 'TEXT' | 'ATTACHMENT' | 'TEXT_ATTACHMENT';
export type MessageStatus = 'SENT' | 'DELIVERED' | 'READ';
export type UsuarioType = 'CLIENT' | 'ADMIN' | 'SYSTEM';
export type ChatSocketRole = 'ADMIN' | 'CLIENT';
export type AttachmentKind = 'IMAGE' | 'PDF' | 'DOCX' | 'OTHER';
export type AttachmentProcessingStatus = 'READY' | 'PROCESSING' | 'FAILED';

export type ChatMetadata = Record<string, unknown>;

export interface ChatAttachmentCreate {
  storage_path: string;
  thumbnail_path?: string | null;
  file_name: string;
  file_size: number;
  mime_type?: string | null;
  metadata_json?: ChatMetadata | null;
}

export interface ChatAttachment extends ChatAttachmentCreate {
  id_attachment: number;
  thumbnail_sas_url?: string | null;
  kind: AttachmentKind;
  preview_path?: string | null;
  preview_sas_url?: string | null;
  preview_mime_type?: string | null;
  preview_width?: number | null;
  preview_height?: number | null;
  original_width?: number | null;
  original_height?: number | null;
  page_count?: number | null;
  processing_status?: AttachmentProcessingStatus;
  created_at: string;
}

export interface ClienteChatMessage {
  id_message: number;
  id_conversation: number;
  id_usuario: string;
  nombre_usuario?: string | null;
  usuario_type: UsuarioType;
  message_type: MessageType;
  body_text?: string | null;
  created_at: string;
  updated_at?: string | null;
  status: MessageStatus;
  id_replied_message?: number | null;
  metadata_json?: ChatMetadata | null;
  attachments: ChatAttachment[];
}

export type ChatMessage = ClienteChatMessage;

export interface ClienteChatMessagesResponse {
  items: ClienteChatMessage[];
  has_more: boolean;
  next_cursor_id?: number | null;
}

export interface ChatSendMessageRequest {
  body_text?: string | null;
  message_type: MessageType;
  attachments: ChatAttachmentCreate[];
  id_replied_message?: number | null;
  metadata_json?: ChatMetadata | null;
}

export type ClienteSendMessageRequest = ChatSendMessageRequest;

export interface ClienteUnreadCountResponse {
  unread_count_client: number;
}

export interface ClienteMarkReadResponse {
  marked_read_count: number;
}

export interface AttachmentDownloadUrlResponse {
  id_attachment: number;
  download_sas_url: string;
}

export type ChatSocketEventType =
  | 'connection.accepted'
  | 'connection.error'
  | 'ping'
  | 'pong'
  | 'message.created'
  | 'message.edited'
  | 'messages.read'
  | 'attachment.preview.updated'
  | 'client.presence.updated'
  | 'clients.presence.snapshot'
  | 'unread_count.updated'
  | 'admin_unread_count.updated'
  | 'admin_user_unread_count.updated'
  | 'conversation.updated';

export interface ChatSocketEvent<TPayload = unknown> {
  type: ChatSocketEventType;
  payload: TPayload;
  occurred_at: string;
}

export interface ChatConnectionAcceptedPayload {
  connection_id: string;
  id_usuario: string;
  role: ChatSocketRole;
}

export interface ChatConnectionErrorPayload {
  detail: string;
}

export interface ChatMessageEventPayload<TMessage extends ChatMessage = ChatMessage> {
  id_usuario: string;
  message: TMessage;
}

export interface ChatAttachmentPreviewUpdatedPayload {
  id_usuario: string;
  id_message: number;
  attachment: ChatAttachment;
}

export interface ChatUnreadCountUpdatedPayload {
  unread_count_client: number;
}

export interface ChatPingPayload extends ChatMetadata {
  client_ts: number;
}

export function buildMessageType(text: string | null | undefined, attachmentsCount: number): MessageType {
  const hasText = !!text?.trim();
  const hasAttachments = attachmentsCount > 0;

  if (hasText && hasAttachments) return 'TEXT_ATTACHMENT';
  if (hasAttachments) return 'ATTACHMENT';
  return 'TEXT';
}

export function hasValidChatContent(text: string | null | undefined, attachmentsCount: number): boolean {
  return !!text?.trim() || attachmentsCount > 0;
}
